import { useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import {
  Box, Typography, Chip, Avatar, CircularProgress, Divider, Paper, Tabs, Tab,
} from '@mui/material'
import {
  ArrowBack as BackIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Inventory2Outlined as ProductIcon,
  CalendarTodayOutlined as CalendarIcon,
  CurrencyRupeeOutlined as RupeeIcon,
  ReceiptLongOutlined as InvoiceIcon,
  AccountBalanceWalletOutlined as WalletIcon,
  CreditCardOutlined as CardIcon,
} from '@mui/icons-material'
import { useQuery } from '@tanstack/react-query'
import { themeColors } from '../app/layout/theme'
import { retentionApi } from '../lib/api/retentionApi'
import { BreadcrumbNav } from '../components/shared/BreadcrumbNav'
import type { OrderPaymentStatus } from '../types/verificationTypes'
import { SalesCustomerRetentionDetailPage } from './SalesCustomerRetentionDetailPage'
import { maskPhoneNumber } from '../utils/phoneUtils'

// ── Badge ─────────────────────────────────────────────────────────────────────

const PAYMENT_CONFIG: Record<OrderPaymentStatus, { bg: string; color: string; border: string }> = {
  'Pending':  { bg: '#FFF8E1', color: '#F57F17', border: '#FFD54F' },
  'Partial':  { bg: '#E3F2FD', color: '#1565C0', border: '#90CAF9' },
  'Paid':     { bg: '#E8F5E9', color: '#2E7D32', border: '#A5D6A7' },
  'Refunded': { bg: '#F3E5F5', color: '#7B1FA2', border: '#CE93D8' },
}

function PaymentBadge({ status }: { status: OrderPaymentStatus }) {
  const cfg = PAYMENT_CONFIG[status] ?? { bg: '#F5F5F5', color: '#616161', border: '#E0E0E0' }
  return (
    <Chip
      label={status}
      size="small"
      sx={{
        borderRadius: '6px', bgcolor: cfg.bg, color: cfg.color,
        border: `1px solid ${cfg.border}`, fontSize: '12px', fontWeight: 700,
        height: 24, '& .MuiChip-label': { px: '10px' },
      }}
    />
  )
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function initials(name: string) {
  return name.split(' ').slice(0, 2).map(n => n[0]?.toUpperCase() ?? '').join('')
}
const AVATAR_COLORS = ['#0070d2', '#2e7d32', '#7b1fa2', '#f57c00', '#c62828', '#00838f']
function avatarColor(name: string) {
  let h = 0; for (const c of name) h = c.charCodeAt(0) + ((h << 5) - h)
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length]
}
function formatDate(iso: string | null | undefined) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}
function fmtCurrency(n: number | string | null | undefined) {
  if (n == null || n === '' || n === '—') return '—'
  const val = typeof n === 'string' ? parseFloat(n) : n
  if (isNaN(val)) return '—'
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(val)
}

// ── Sub-components ────────────────────────────────────────────────────────────

function SectionCard({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${themeColors.border}`,
        borderRadius: '12px',
        overflow: 'hidden',
        bgcolor: themeColors.surface,
      }}
    >
      <Box sx={{ px: 2.5, py: 1.75, borderBottom: `1px solid ${themeColors.borderLight}`, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ color: themeColors.primary, display: 'flex', fontSize: 18 }}>{icon}</Box>
        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: themeColors.text.primary, letterSpacing: '0.1px' }}>
          {title}
        </Typography>
      </Box>
      <Box sx={{ px: 2.5, py: 2 }}>{children}</Box>
    </Paper>
  )
}

function InfoRow({ label, value, valueColor }: { label: string; value: React.ReactNode; valueColor?: string }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.85 }}>
      <Typography sx={{ fontSize: '12px', color: themeColors.text.secondary, fontWeight: 500, flexShrink: 0, minWidth: 120 }}>
        {label}
      </Typography>
      <Box sx={{ textAlign: 'right' }}>
        {typeof value === 'string' || typeof value === 'number' ? (
          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: valueColor ?? themeColors.text.primary }}>
            {value}
          </Typography>
        ) : value}
      </Box>
    </Box>
  )
}

function InfoDivider() {
  return <Divider sx={{ borderColor: themeColors.borderLight, my: 0.25 }} />
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function RecentOrderDetailPage() {
  const { id }   = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [mainTab, setMainTab] = useState(0)

  // 1. Local retention record
  const { data: retentionMatch, isLoading: isRetentionLoading } = useQuery({
    queryKey: ['recent-order-detail-single', id],
    queryFn: () => retentionApi.getById(id!),
    enabled: !!id,
    staleTime: 60_000,
  })

  const orderIdToFetch = retentionMatch?.orderId

  // 2. Gateway Order Details API (only enabled once orderId is known)
  const { data: gatewayOrder, isLoading: isGatewayLoading } = useQuery({
    queryKey: ['gateway-order-detail', orderIdToFetch],
    queryFn: () => retentionApi.getGatewayOrderDetail(orderIdToFetch!),
    enabled: !!orderIdToFetch,
    staleTime: 60_000,
  })

  const isLoading = isRetentionLoading || (!!orderIdToFetch && isGatewayLoading)

  // ── Loading state ──────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <CircularProgress size={36} sx={{ color: themeColors.primary }} />
      </Box>
    )
  }

  // ── Not found ──────────────────────────────────────────────────────────────
  if (!retentionMatch && !gatewayOrder) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
        <Typography sx={{ fontSize: '16px', fontWeight: 600, color: themeColors.text.primary }}>Order not found</Typography>
        <Box
          component="button"
          onClick={() => navigate('/recency-orders')}
          sx={{ fontSize: '13px', color: themeColors.primary, cursor: 'pointer', border: 'none', background: 'none', fontWeight: 600 }}
        >
          ← Back to Recency Dashboard
        </Box>
      </Box>
    )
  }

  // Extract fields with fallbacks to '-'
  const uniqueOrderId  = gatewayOrder?.unique_order_id || retentionMatch?.orderId || '—'
  const customerName   = gatewayOrder?.customer_outlet?.legalName || gatewayOrder?.customer_outlet?.name || retentionMatch?.customerName || '—'
  const companyName    = retentionMatch?.outletName || gatewayOrder?.customer_outlet?.name || '—'
  const customerType   = gatewayOrder?.customer_type_object?.name || gatewayOrder?.customer_outlet?.customerType?.name || '—'
  const mobile         = gatewayOrder?.customer_outlet?.mobile || retentionMatch?.mobile || '—'
  const email          = gatewayOrder?.customer_outlet?.email || retentionMatch?.email || '—'
  const orderDate      = gatewayOrder?.createdAt || retentionMatch?.orderedAt || retentionMatch?.createdAt
  const deliveryDate   = gatewayOrder?.delivery_date_time
  const invoiceNumber  = gatewayOrder?.order_invoice?.invoiceNumber || '—'
  const orderStatus    = gatewayOrder?.order_status_data?.name || '—'
  const paymentStatus  = gatewayOrder?.payment_status || '—'
  const paymentMethod  = gatewayOrder?.payment_method || '—'
  const items          = (gatewayOrder?.order_items as any[]) ?? []
  const shipping       = gatewayOrder?.shipping_address
  const warehouse      = gatewayOrder?.ware_house
  const slot           = gatewayOrder?.slot
  const deliveryAgent  = gatewayOrder?.user
  const proofUrl       = gatewayOrder?.delivery_proof_url

  const subTotal       = gatewayOrder?.order_invoice?.pre_tax ?? gatewayOrder?.subTotal ?? (retentionMatch?.orderValue ? parseFloat(retentionMatch.orderValue) : null)
  const taxAmount      = gatewayOrder?.order_invoice?.tax_amount ?? gatewayOrder?.tax ?? null
  const deliveryCost   = gatewayOrder?.order_invoice?.delivery_cost ?? gatewayOrder?.delivery_cost ?? null
  const totalSaving    = gatewayOrder?.total_saving ?? null
  const netAmount      = gatewayOrder?.order_invoice?.total ?? gatewayOrder?.net_amount ?? subTotal

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: themeColors.background }}>

      {/* ── Top header bar ── */}
      <Box
        sx={{
          px: 3, pt: 1.5, pb: 0,
          bgcolor: themeColors.surface,
          borderBottom: `1px solid ${themeColors.border}`,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <BreadcrumbNav
            items={[
              { label: 'Recency Dashboard', to: '/recency-orders' },
            ]}
            current={uniqueOrderId ? `Order ${uniqueOrderId}` : 'Order Details'}
            showBack={true}
            onBack={() => navigate('/recency-orders')}
          />
          <Chip
            label={orderStatus}
            size="small"
            sx={{
              borderRadius: '6px', bgcolor: '#E8F5E9', color: '#2E7D32',
              border: '1px solid #A5D6A7', fontSize: '12px', fontWeight: 700,
              height: 24, '& .MuiChip-label': { px: '10px' },
            }}
          />
        </Box>

        <Tabs
          value={mainTab}
          onChange={(_e, v) => setMainTab(v)}
          sx={{
            minHeight: 40,
            '& .MuiTab-root': {
              minHeight: 40, textTransform: 'none', fontWeight: 700, fontSize: '13px', px: 2,
              color: 'text.secondary', transition: 'color 0.2s'
            },
            '& .MuiTabs-indicator': { bgcolor: themeColors.primary, height: 2, borderRadius: '2px 2px 0 0' },
            '& .Mui-selected': { color: `${themeColors.primary} !important` },
          }}
        >
          <Tab label="Order Details" />
          <Tab label="Customer Activity & Info" />
        </Tabs>
      </Box>

      {/* ── Tab Content ── */}
      {mainTab === 0 ? (
        /* ── Scrollable content: Order Details ── */
        <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>

        {/* Customer Header Hero */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Avatar
            sx={{
              width: 48, height: 48, fontSize: '16px', fontWeight: 700,
              bgcolor: avatarColor(customerName),
              border: `2px solid ${themeColors.surface}`,
              boxShadow: themeColors.shadow.md,
            }}
          >
            {initials(customerName)}
          </Avatar>
          <Box>
            <Typography sx={{ fontSize: '20px', fontWeight: 700, color: themeColors.text.primary, letterSpacing: '-0.3px' }}>
              {customerName}
            </Typography>
            <Typography sx={{ fontSize: '13px', color: themeColors.text.secondary }}>
              {companyName} • <span style={{ fontWeight: 600 }}>{customerType}</span>
            </Typography>
          </Box>
        </Box>

        {/* ── Top Grid: Customer Info & Delivery Address ── */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5, mb: 2.5 }}>

          {/* Outlet & Customer Info */}
          <SectionCard title="Customer & Outlet Information" icon={<PersonIcon fontSize="inherit" />}>
            <InfoRow label="Legal / Customer Name" value={customerName} />
            <InfoDivider />
            <InfoRow
              label="Outlet Name"
              value={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <BusinessIcon sx={{ fontSize: 14, color: themeColors.text.secondary }} />
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, color: themeColors.text.primary }}>
                    {companyName}
                  </Typography>
                </Box>
              }
            />
            <InfoDivider />
            <InfoRow label="Customer Type" value={customerType} />
            <InfoDivider />
            <InfoRow
              label="Phone"
              value={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PhoneIcon sx={{ fontSize: 13, color: themeColors.text.secondary }} />
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, color: themeColors.text.primary }}>
                    {maskPhoneNumber(mobile)}
                  </Typography>
                </Box>
              }
            />
            <InfoDivider />
            <InfoRow
              label="Email"
              value={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <EmailIcon sx={{ fontSize: 13, color: themeColors.text.secondary }} />
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, color: themeColors.text.primary }}>
                    {email}
                  </Typography>
                </Box>
              }
            />
            <InfoDivider />
            <InfoRow label="Assigned User" value={retentionMatch?.ownerName || 'Unassigned'} />
            <InfoDivider />
            <InfoRow label="Ordered By" value={retentionMatch?.orderedByName || '—'} />
          </SectionCard>

          {/* Shipping & Delivery Info */}
          <SectionCard title="Fulfillment & Shipping Details" icon={<CalendarIcon fontSize="inherit" />}>
            <InfoRow
              label="Order Date"
              value={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CalendarIcon sx={{ fontSize: 13, color: themeColors.text.secondary }} />
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, color: themeColors.text.primary }}>
                    {formatDate(orderDate)}
                  </Typography>
                </Box>
              }
            />
            <InfoDivider />
            <InfoRow
              label="Delivery Date"
              value={formatDate(deliveryDate)}
            />
            <InfoDivider />
            <InfoRow label="Warehouse" value={warehouse?.name || '—'} />
            <InfoDivider />
            <InfoRow
              label="Delivery Slot"
              value={slot ? `${slot.from_time} - ${slot.to_time}` : '—'}
            />
            <InfoDivider />
            <InfoRow
              label="Delivery Agent"
              value={deliveryAgent ? `${deliveryAgent.full_name} (${deliveryAgent.mobile})` : '—'}
            />
            <InfoDivider />
            <InfoRow
              label="Shipping Address"
              value={
                shipping
                  ? `${shipping.full_address || ''}, ${shipping.city_name || ''}, ${shipping.state_name || ''} ${shipping.pincode || ''}`
                  : '—'
              }
            />
          </SectionCard>
        </Box>

        {/* ── Order Items Table ── */}
        <Box sx={{ mb: 2.5 }}>
          <SectionCard title={`Order Items (${items.length})`} icon={<ProductIcon fontSize="inherit" />}>
            {items.length === 0 ? (
              <Typography sx={{ fontSize: '13px', color: themeColors.text.secondary }}>No item details available.</Typography>
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Box
                  component="table"
                  sx={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '12px',
                    '& th': {
                      py: 1, px: 1.5,
                      textAlign: 'left',
                      fontWeight: 700,
                      color: themeColors.text.secondary,
                      borderBottom: `1px solid ${themeColors.border}`,
                      bgcolor: themeColors.background,
                    },
                    '& td': {
                      py: 1.25, px: 1.5,
                      borderBottom: `1px solid ${themeColors.borderLight}`,
                      color: themeColors.text.primary,
                    },
                  }}
                >
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>SKU / HSN</th>
                      <th>Quantity</th>
                      <th style={{ textAlign: 'right' }}>Price</th>
                      <th style={{ textAlign: 'right' }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item: any) => (
                      <tr key={item.id}>
                        <td>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                            {item.product_images && (
                              <Box
                                component="img"
                                src={item.product_images}
                                alt={item.name}
                                sx={{ width: 36, height: 36, borderRadius: '6px', objectFit: 'cover', border: `1px solid ${themeColors.border}` }}
                              />
                            )}
                            <Box>
                              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: themeColors.text.primary }}>
                                {item.name}
                              </Typography>
                              {item.category_name && (
                                <Typography sx={{ fontSize: '11px', color: themeColors.text.secondary }}>
                                  {item.category_name}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </td>
                        <td>
                          <Typography sx={{ fontSize: '11px', fontFamily: 'monospace', color: themeColors.text.secondary }}>
                            SKU: {item.sku || '—'}<br />HSN: {item.hsn_code || '—'}
                          </Typography>
                        </td>
                        <td>
                          <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>
                            {item.quantity} {item.unit || ''}
                          </Typography>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>
                            {fmtCurrency(item.item_price)}
                          </Typography>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <Typography sx={{ fontSize: '13px', fontWeight: 700, color: themeColors.text.primary }}>
                            {fmtCurrency(item.total_item_price)}
                          </Typography>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Box>
              </Box>
            )}
          </SectionCard>
        </Box>

        {/* ── Financial & Payment Summary ── */}
        <SectionCard title="Payment & Order Financials" icon={<WalletIcon fontSize="inherit" />}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 0 }}>

            {/* Column 1: Pricing */}
            <Box sx={{ pr: { sm: 3 }, borderRight: { sm: `1px solid ${themeColors.borderLight}` } }}>
              <InfoRow label="Subtotal (Pre-Tax)" value={fmtCurrency(subTotal)} />
              <InfoDivider />
              <InfoRow label="Tax Amount" value={fmtCurrency(taxAmount)} />
              <InfoDivider />
              <InfoRow label="Delivery Cost" value={fmtCurrency(deliveryCost)} />
              <InfoDivider />
              <InfoRow
                label="Total Savings"
                value={
                  <Typography sx={{ fontSize: '13px', fontWeight: 700, color: themeColors.success }}>
                    {fmtCurrency(totalSaving)}
                  </Typography>
                }
              />
              <InfoDivider />
              <InfoRow
                label="Net Amount"
                value={
                  <Typography sx={{ fontSize: '16px', fontWeight: 800, color: themeColors.primary }}>
                    {fmtCurrency(netAmount)}
                  </Typography>
                }
              />
            </Box>

            {/* Column 2: Payment Status */}
            <Box sx={{ px: { sm: 3 }, pt: { xs: 1, sm: 0 }, borderRight: { md: `1px solid ${themeColors.borderLight}` } }}>
              <InfoRow
                label="Payment Method"
                value={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CardIcon sx={{ fontSize: 13, color: themeColors.text.secondary }} />
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: themeColors.text.primary }}>
                      {paymentMethod}
                    </Typography>
                  </Box>
                }
              />
              <InfoDivider />
              <InfoRow
                label="Payment Status"
                value={
                  <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#F57F17' }}>
                    {paymentStatus}
                  </Typography>
                }
              />
              <InfoDivider />
              <InfoRow
                label="Invoice Number"
                value={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <InvoiceIcon sx={{ fontSize: 13, color: themeColors.text.secondary }} />
                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: themeColors.text.primary, fontFamily: 'monospace' }}>
                      {invoiceNumber}
                    </Typography>
                  </Box>
                }
              />
            </Box>
          </Box>
        </SectionCard>
      </Box>
      ) : (
        /* ── Read-Only Customer Activity & History ── */
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          <SalesCustomerRetentionDetailPage
            retentionRecordId={retentionMatch?.id ?? id}
            readOnly={true}
          />
        </Box>
      )}
    </Box>
  )
}
